// frontend/src/pages/OrdineForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

/* ===== Copia intestazione -> spedizione ===== */
const BILLING_TO_SHIPPING_MAP = {
  fatt_ragione_sociale: "sped_destinatario",
  fatt_indirizzo: "sped_indirizzo",
  fatt_cap: "sped_cap",
  fatt_citta: "sped_citta",
  fatt_provincia: "sped_provincia",
  fatt_nazione: "sped_nazione",
};
function copyBillingToShipping(fatt, sped) {
  const updates = {};
  Object.entries(BILLING_TO_SHIPPING_MAP).forEach(([src, dst]) => {
    updates[dst] = fatt?.[src] ?? "";
  });
  return { ...sped, ...updates };
}
/* ============================================ */

const OrdineForm = () => {
  const [prodotti, setProdotti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [selectedClient, setSelectedClient] = useState(""); // opzionale
  const [items, setItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("BONIFICO");
  const [messaggio, setMessaggio] = useState("");

  // Per chi è l’ordine (etichetta veloce)
  const [perNome, setPerNome] = useState("");

  // Fatturazione
  const [fatt, setFatt] = useState({
    fatt_ragione_sociale: "",
    fatt_piva: "",
    fatt_cf: "",
    fatt_indirizzo: "",
    fatt_cap: "",
    fatt_citta: "",
    fatt_provincia: "",
    fatt_nazione: "",
    fatt_email: "",
    fatt_pec: "",
    fatt_sdi: "",
  });

  // Spedizione
  const [sped, setSped] = useState({
    sped_destinatario: "",
    sped_indirizzo: "",
    sped_cap: "",
    sped_citta: "",
    sped_provincia: "",
    sped_nazione: "",
    sped_telefono: "",
    sped_note: "",
  });

  // ✅ Spunta “spedizione = intestazione”
  const [shipSameAsBilling, setShipSameAsBilling] = useState(false);
  // sincronizza quando cambia la spunta o cambiano i campi di fatturazione
  useEffect(() => {
    if (!shipSameAsBilling) return;
    setSped((s) => copyBillingToShipping(fatt, s));
  }, [
    shipSameAsBilling,
    fatt.fatt_ragione_sociale,
    fatt.fatt_indirizzo,
    fatt.fatt_cap,
    fatt.fatt_citta,
    fatt.fatt_provincia,
    fatt.fatt_nazione,
  ]);

  const nav = useNavigate();

useEffect(() => {
  const token = localStorage.getItem("access");
  const headers = { Authorization: `Bearer ${token}` };

  const roleToListino = (role) => {
    const r = (role || "").toUpperCase();
    if (r === "AZIENDA") return "azienda";
    if (r === "FRANCHISING") return "franchising";
    if (r === "PV") return "pv";
    return "cliente"; // AGENTE o altri
  };

  const load = async () => {
    try {
      const meRes = await fetch(`${API_URL}/api/me/`, { headers });
      const me = await meRes.json();
      const tipo = roleToListino(me?.role);

      const prodRes = await fetch(
        `${API_URL}/api/prodotti/?tipo_listino=${encodeURIComponent(tipo)}`,
        { headers }
      );
      const prod = await prodRes.json();
      setProdotti(prod);
    } catch (e) {
      alert("Errore caricamento prodotti");
    }

    // opzionale: lista clienti tuoi (se vuoi selezionarlo)
    fetch(`${API_URL}/api/clienti/`, { headers })
      .then((res) => res.json())
      .then(setClienti)
      .catch(() => {});
  };

  load();
}, []);

  const aggiungiProdotto = (id) => {
    if (!items.some((item) => item.product === id)) {
      setItems([...items, { product: id, quantity: 1 }]);
    }
  };

  const cambiaQuantita = (i, val) => {
    const q = Number(val);
    const arr = [...items];
    arr[i].quantity = isNaN(q) || q <= 0 ? 1 : q;
    setItems(arr);
  };

  const rimuovi = (i) => {
    const arr = [...items];
    arr.splice(i, 1);
    setItems(arr);
  };

  const invia = async () => {
    if (items.length === 0) return alert("Aggiungi almeno un prodotto.");
    if (!perNome && !selectedClient)
      return alert("Compila 'Per chi è l’ordine' oppure seleziona un cliente.");

    // se la spunta è attiva, assicuriamoci di spedire i campi aggiornati
    const spedFinal = shipSameAsBilling ? copyBillingToShipping(fatt, sped) : sped;

    // Payload compatibile con back nuovo (ordini/miei/) e legacy (ordini/crea/)
    const payload = {
      status: "SUBMITTED", // crea e invia direttamente
      payment_method: paymentMethod,
      messaggio_agente: messaggio,
      items: items.map((it) => ({
        product: it.product,
        quantity: Number(it.quantity) || 1,
      })),
      per_nome: perNome || undefined,
      // fatturazione & spedizione
      ...fatt,
      ...spedFinal,
      // compat con backend precedente:
      cliente: selectedClient || undefined,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    };

    // 1) prova endpoint nuovo
    let res = await fetch(`${API_URL}/api/ordini/miei/`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    // 2) fallback su endpoint precedente se 404/405
    if (!res.ok && (res.status === 404 || res.status === 405)) {
      res = await fetch(`${API_URL}/api/ordini/crea/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
    }

    if (res.ok) {
      alert("✅ Ordine inviato");
      nav("/ordini");
      return;
    }
    const err = await res.json().catch(() => ({}));
    alert("❌ Errore: " + (err.detail || JSON.stringify(err)));
  };

  return (
    <div className="container mt-4">
      <button className="btn btn-outline-secondary mb-3" onClick={() => nav("/ordini")}>
        ← Indietro
      </button>

      <h2>📦 Nuovo Ordine</h2>

      {/* Per chi è l’ordine / Cliente opzionale */}
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Per chi è l’ordine (etichetta breve)</label>
          <input
            className="form-control"
            placeholder="Es. Bar Rossi"
            value={perNome}
            onChange={(e) => setPerNome(e.target.value)}
          />
          <small className="text-muted">
            Se non compili, puoi selezionare un cliente qui a fianco (opzionale).
          </small>
        </div>

        <div className="col-md-6">
          <label className="form-label">Cliente (opzionale)</label>
          <select
            className="form-select"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="">-- Nessuno --</option>
            {clienti.map((c) => (
              <option key={c.id} value={c.id}>
                {c.store_name || `${c.first_name} ${c.last_name}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Metodo pagamento + Messaggio */}
      <div className="row g-3 mt-2">
        <div className="col-md-6">
          <label className="form-label">Metodo di pagamento</label>
          <select
            className="form-select"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <option value="BONIFICO">Bonifico Bancario</option>
            <option value="RITIRO">Contanti / Carta</option>
            <option value="CONSEGNA">Pagamento alla consegna</option>
            <option value="ONLINE">Pagamento Online</option>
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Messaggio al MADRE</label>
          <textarea
            className="form-control"
            rows="1"
            value={messaggio}
            onChange={(e) => setMessaggio(e.target.value)}
          />
        </div>
      </div>

      {/* Fatturazione */}
      <div className="card mt-3">
        <div className="card-header">Dati Fatturazione</div>
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label">Ragione sociale</label>
              <input
                className="form-control"
                value={fatt.fatt_ragione_sociale}
                onChange={(e) => setFatt({ ...fatt, fatt_ragione_sociale: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">P.IVA</label>
              <input
                className="form-control"
                value={fatt.fatt_piva}
                onChange={(e) => setFatt({ ...fatt, fatt_piva: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Cod. Fiscale</label>
              <input
                className="form-control"
                value={fatt.fatt_cf}
                onChange={(e) => setFatt({ ...fatt, fatt_cf: e.target.value })}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">Indirizzo</label>
              <input
                className="form-control"
                value={fatt.fatt_indirizzo}
                onChange={(e) => setFatt({ ...fatt, fatt_indirizzo: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">CAP</label>
              <input
                className="form-control"
                value={fatt.fatt_cap}
                onChange={(e) => setFatt({ ...fatt, fatt_cap: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Città</label>
              <input
                className="form-control"
                value={fatt.fatt_citta}
                onChange={(e) => setFatt({ ...fatt, fatt_citta: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Prov.</label>
              <input
                className="form-control"
                value={fatt.fatt_provincia}
                onChange={(e) => setFatt({ ...fatt, fatt_provincia: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Nazione</label>
              <input
                className="form-control"
                value={fatt.fatt_nazione}
                onChange={(e) => setFatt({ ...fatt, fatt_nazione: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Email fatturazione</label>
              <input
                className="form-control"
                value={fatt.fatt_email}
                onChange={(e) => setFatt({ ...fatt, fatt_email: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">PEC</label>
              <input
                className="form-control"
                value={fatt.fatt_pec}
                onChange={(e) => setFatt({ ...fatt, fatt_pec: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Codice SDI</label>
              <input
                className="form-control"
                value={fatt.fatt_sdi}
                onChange={(e) => setFatt({ ...fatt, fatt_sdi: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Spedizione */}
      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Dati Spedizione</span>
          <div className="form-check m-0">
            <input
              id="sameAsBilling"
              className="form-check-input"
              type="checkbox"
              checked={shipSameAsBilling}
              onChange={(e) => setShipSameAsBilling(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="sameAsBilling">
              Spedizione uguale a intestazione
            </label>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label">Destinatario</label>
              <input
                className="form-control"
                value={sped.sped_destinatario}
                onChange={(e) => setSped({ ...sped, sped_destinatario: e.target.value })}
                disabled={shipSameAsBilling}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Telefono</label>
              <input
                className="form-control"
                value={sped.sped_telefono}
                onChange={(e) => setSped({ ...sped, sped_telefono: e.target.value })}
                // telefono resta sempre editabile
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">Indirizzo</label>
              <input
                className="form-control"
                value={sped.sped_indirizzo}
                onChange={(e) => setSped({ ...sped, sped_indirizzo: e.target.value })}
                disabled={shipSameAsBilling}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">CAP</label>
              <input
                className="form-control"
                value={sped.sped_cap}
                onChange={(e) => setSped({ ...sped, sped_cap: e.target.value })}
                disabled={shipSameAsBilling}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Città</label>
              <input
                className="form-control"
                value={sped.sped_citta}
                onChange={(e) => setSped({ ...sped, sped_citta: e.target.value })}
                disabled={shipSameAsBilling}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Prov.</label>
              <input
                className="form-control"
                value={sped.sped_provincia}
                onChange={(e) => setSped({ ...sped, sped_provincia: e.target.value })}
                disabled={shipSameAsBilling}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Nazione</label>
              <input
                className="form-control"
                value={sped.sped_nazione}
                onChange={(e) => setSped({ ...sped, sped_nazione: e.target.value })}
                disabled={shipSameAsBilling}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label">Note consegna</label>
              <textarea
                className="form-control"
                rows={2}
                value={sped.sped_note}
                onChange={(e) => setSped({ ...sped, sped_note: e.target.value })}
                // note restano sempre editabili
              />
            </div>
          </div>
        </div>
      </div>

      {/* Prodotti disponibili */}
      <div className="mb-3 mt-3">
        <label className="form-label">Prodotti disponibili</label>
        <ul className="list-group">
          {prodotti.map((p) => (
            <li
              key={p.id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              {p.name}
           <button
           type="button"
           className="btn btn-sm btn-outline-primary"
           onClick={() => aggiungiProdotto(p.id)}
            >

                Aggiungi
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Righe selezionate */}
      <h5 className="mt-4">🧾 Prodotti aggiunti</h5>
      {items.length === 0 && <p>Nessun prodotto selezionato</p>}
      {items.map((it, i) => (
        <div key={i} className="d-flex align-items-center mb-2">
          <select className="form-select me-2" value={it.product} disabled>
            {prodotti.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            className="form-control me-2"
            value={it.quantity}
            min="1"
            step="1"
            onChange={(e) => cambiaQuantita(i, e.target.value)}
          />
          <button
           type="button" 
           className="btn btn-danger" onClick={() => rimuovi(i)}>
            🗑
          </button>
        </div>
      ))}

      <button
       type="button" 
       className="btn btn-success mt-3" onClick={invia}>
        ✅ Invia Ordine
      </button>
    </div>
  );
};

export default OrdineForm;
